import React, { useState, useRef } from 'react';
import { newtonAPI, handleAPIError } from '../api';

const NewtonSimple = () => {
  // Estados del formulario
  const [marketplace, setMarketplace] = useState('Amazon');
  const [executionMode, setExecutionMode] = useState('immediate');
  const [file, setFile] = useState(null);
  const [formData, setFormData] = useState({
    histMonth: new Date().getMonth() + 1,
    histYear: new Date().getFullYear(),
    skuCol: '',
    productCol: '',
    quantityCol: '',
    globalUnitsCol: '',
    filterOption: 'delivered',
    forecastMode: 'v25_lite',
    useSample: false,
    // Automatización
    jobName: '',
    priority: 'medium',
    notificationEmails: ''
  });
  
  // Estados de ejecución
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState('');
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  
  const fileInputRef = useRef(null);
  
  // Configuración de placeholders por marketplace
  const marketplacePlaceholders = {
    Amazon: {
      skuCol: 'asin',
      productCol: 'product-name',
      quantityCol: 'quantity',
      globalUnitsCol: 'quantity'
    },
    Mercadolibre: {
      skuCol: '# de publicación',
      productCol: 'título de la publicación',
      quantityCol: 'unidades',
      globalUnitsCol: 'unidades'
    },
    Liverpool: {
      skuCol: 'sku',
      productCol: 'product_name',
      quantityCol: 'quantity',
      globalUnitsCol: 'quantity'
    },
    Claro: {
      skuCol: 'sku',
      productCol: 'product_name',
      quantityCol: 'quantity',
      globalUnitsCol: 'monto'
    },
    Walmart: {
      skuCol: 'sku',
      productCol: 'product_name',
      quantityCol: 'quantity',
      globalUnitsCol: 'venta bruta'
    }
  };
  
  const currentPlaceholders = marketplacePlaceholders[marketplace] || marketplacePlaceholders.Amazon;
  
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };
  
  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    setFile(selectedFile);
    
    // Auto-generar nombre de trabajo si está en modo automatización
    if (executionMode === 'automation' && selectedFile) {
      const jobName = `Pronóstico ${marketplace} ${formData.histMonth.toString().padStart(2, '0')}/${formData.histYear}`;
      handleInputChange('jobName', jobName);
    }
  };
  
  const executeNewtonForecast = async () => {
    try {
      setLoading(true);
      setError(null);
      setProgress(0);
      setProgressMessage('Iniciando pronóstico...');
      
      if (!file) {
        throw new Error('Por favor selecciona un archivo de datos');
      }
      
      // Validar columnas obligatorias (usar placeholders si están vacías)
      const skuCol = formData.skuCol.trim() || currentPlaceholders.skuCol;
      const productCol = formData.productCol.trim() || currentPlaceholders.productCol;
      const quantityCol = formData.quantityCol.trim() || currentPlaceholders.quantityCol;
      const globalUnitsCol = formData.globalUnitsCol.trim() || currentPlaceholders.globalUnitsCol;
      
      if (!skuCol || !productCol || !quantityCol) {
        throw new Error('Por favor completa todos los nombres de columnas obligatorias');
      }
      
      // Preparar FormData para el archivo
      const formDataToSend = new FormData();
      formDataToSend.append('file', file);
      formDataToSend.append('marketplace', marketplace);
      formDataToSend.append('hist_month', formData.histMonth);
      formDataToSend.append('hist_year', formData.histYear);
      formDataToSend.append('sku_col', skuCol);
      formDataToSend.append('product_col', productCol);
      formDataToSend.append('quantity_col', quantityCol);
      formDataToSend.append('global_units_col', globalUnitsCol);
      formDataToSend.append('filter_option', formData.filterOption);
      formDataToSend.append('forecast_mode', formData.forecastMode);
      formDataToSend.append('use_sample', formData.useSample);
      
      if (executionMode === 'automation') {
        // Modo automatización
        formDataToSend.append('job_name', formData.jobName);
        formDataToSend.append('priority', formData.priority);
        formDataToSend.append('notification_emails', formData.notificationEmails);
        
        const response = await newtonAPI.forecastEnqueue(formDataToSend);
        
        if (response.success) {
          setProgress(100);
          setProgressMessage('Trabajo encolado exitosamente');
          setResults({
            type: 'automation',
            jobId: response.job_id,
            message: 'El pronóstico se está ejecutando en segundo plano. Puedes monitorear el progreso en la sección de Automatizaciones.'
          });
        } else {
          throw new Error('Error enviando trabajo a automatización');
        }
      } else {
        // Modo inmediato con progreso simulado
        const response = await newtonAPI.forecastRun(formDataToSend, (progressData) => {
          setProgress(progressData.progress || 0);
          setProgressMessage(progressData.message || 'Procesando...');
        });
        
        if (response.success) {
          setProgress(100);
          setProgressMessage('Pronóstico completado exitosamente');
          setResults({
            type: 'immediate',
            data: response.data
          });
        } else {
          throw new Error('Error ejecutando pronóstico');
        }
      }
    } catch (err) {
      const errorInfo = handleAPIError(err);
      setError(errorInfo.message || 'Error ejecutando pronóstico');
      setProgress(0);
      setProgressMessage('');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="main-container">
      <div className="content-header">
        <h1 className="title-primary">
          🚀 Newton Simple - Pronóstico de Ventas
        </h1>
        <p className="subtitle">
          Interfaz simplificada - Una sola página para generar pronósticos
        </p>
      </div>
      
      <div className="card">
        <h2 className="title-secondary">📋 Configuración del Pronóstico</h2>
        <div className="description">
          Formulario de configuración en desarrollo...
        </div>
        <button
          onClick={executeNewtonForecast}
          disabled={loading || !file}
          className="btn-primary"
          style={{ 
            width: '100%', 
            marginTop: '24px',
            opacity: (loading || !file) ? 0.6 : 1,
            cursor: (loading || !file) ? 'not-allowed' : 'pointer'
          }}
        >
          {loading ? '🔄 Procesando...' : '🚀 GENERAR PRONÓSTICO'}
        </button>
      </div>

      {error && (
        <div className="message message-error">
          {error}
        </div>
      )}

      {loading && progress > 0 && (
        <div className="card">
          <h3 className="title-tertiary">Progreso</h3>
          <div style={{ 
            backgroundColor: 'var(--bg-secondary)', 
            borderRadius: '8px', 
            padding: '16px',
            marginBottom: '16px'
          }}>
            <div style={{ 
              width: `${progress}%`, 
              height: '8px', 
              backgroundColor: 'var(--color-primary-blue)', 
              borderRadius: '4px',
              transition: 'width 0.3s ease'
            }}></div>
            <p className="description" style={{ marginTop: '8px', marginBottom: '0' }}>
              {progressMessage} ({progress}%)
            </p>
          </div>
        </div>
      )}

      {results && (
        <div className="card">
          <h3 className="title-secondary">Resultados</h3>
          <div className="description">
            {results.type === 'automation' ? (
              <div>
                <p><strong>ID del Trabajo:</strong> {results.jobId}</p>
                <p>{results.message}</p>
              </div>
            ) : (
              <pre style={{ 
                backgroundColor: 'var(--bg-secondary)', 
                padding: '16px', 
                borderRadius: '8px',
                overflow: 'auto',
                color: 'var(--text-secondary)',
                fontSize: '14px'
              }}>
                {JSON.stringify(results.data, null, 2)}
              </pre>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NewtonSimple;
